import { EIGENFIELD, EIGENFIELD_MOTION } from './eigenfield-recipe';
import { EIGENFIELD_VARIANTS, type EigenfieldVariant } from './eigenfield-variants';
import type { TextBandFade } from './art-fade';

export type EigenfieldUniformInput = {
  width: number;
  height: number;
  pixelRatio: number;
  artFade: TextBandFade;
  seconds: number;
  /** Which superposition to draw. Omit for the baked recipe — mobile and every
   *  existing caller get exactly what they got before. */
  variant?: EigenfieldVariant;
};

/**
 * Every scalar uniform the shader declares, for a given moment.
 *
 * Pulled out of the component so it is callable from a test: a uniform whose name here
 * does not match one declared in the shader is not a type error, it is a shader that
 * silently reads zero.
 *
 * ── Why mobile does not import this ──────────────────────────────────────────
 * The mobile copy in `mobile/src/components/onboarding/eigenfield-uniforms.ts` is
 * byte-identical apart from carrying a `'worklet'` directive, because it runs on
 * the UI thread inside `useDerivedValue`. Whether Reanimated's Babel plugin
 * workletises a file resolved through a workspace symlink in node_modules is not
 * something this repo can verify without a device, and getting it wrong fails
 * silently at runtime on a surface that already ships.
 *
 * So the duplication is deliberate, and `eigenfield-uniforms-parity.test.ts` in
 * mobile asserts the two produce identical output across sampled inputs. If you
 * are here to "just delete one of them": that test is the reason not to, and the
 * risk is a broken onboarding animation that no test in either project catches.
 */
export function buildEigenfieldUniforms({
  width,
  height,
  pixelRatio,
  artFade,
  seconds,
  variant = EIGENFIELD_VARIANTS.hero,
}: EigenfieldUniformInput) {
  const { periodsSeconds, depth, phases } = EIGENFIELD_MOTION;
  const base = variant.weights;

  // Each mode's weight breathes at the room's own eigenfrequency, slowed to a visible
  // timescale. The ratios are incommensurate, so the sum never repeats.
  const breathe = (i: number) =>
    base[i] * (1 + depth * Math.sin((2 * Math.PI * seconds) / periodsSeconds[i] + phases[i]));

  return {
    resolution: [width, height],
    pixelRatio,
    fieldSize: [EIGENFIELD.fieldWidth, EIGENFIELD.fieldHeight],
    refSize: [EIGENFIELD.referenceWidth, EIGENFIELD.referenceHeight],
    weights: [breathe(0), breathe(1), breathe(2), breathe(3)],
    freq: EIGENFIELD.freq,
    amp: EIGENFIELD.amp,
    warp: EIGENFIELD.warp,
    rotFlag: variant.rot ? 1 : 0,
    tiltCos: Math.cos(variant.tilt),
    tiltSin: Math.sin(variant.tilt),
    ramp0: EIGENFIELD.ramp0,
    rampPow: EIGENFIELD.rampPow,
    densityKnee: EIGENFIELD.densityKnee,
    densityFade: EIGENFIELD.densityFade,
    densityFloor: EIGENFIELD.densityFloor,
    fadeFrac: EIGENFIELD.fadeFrac,
    fadePow: EIGENFIELD.fadePow,
    lutBase: EIGENFIELD.lutBase,
    lutCount: EIGENFIELD.lutCount,
    ground: [EIGENFIELD.ground[0], EIGENFIELD.ground[1], EIGENFIELD.ground[2]],
    artFade: [artFade.start, artFade.end, artFade.minAlpha],
  };
}

/** Names of the `uniform shader` slots, in the order the shader declares them. The
 *  ImageShader children must be passed in exactly this order. */
export const SHADER_CHILD_ORDER = [
  'fieldABC',
  'fieldABCFine',
  'fieldD',
  'lineLut',
] as const;
